import { useMemo } from 'react';
import { parseISO, addDays, startOfWeek, format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

const WeeklyScheduleView = ({ weekSchedules, selectedDate, onDayClick }) => {
  // Get the start of the week for the selected date
  const weekStart = useMemo(() => startOfWeek(selectedDate, { weekStartsOn: 0 }), [selectedDate]);

  // Generate array of 7 days for the week
  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  }, [weekStart]);

  // Generate a list of all unique time slots across all days and boards
  const timeSlots = useMemo(() => {
    const allSlots = new Set();
    Object.values(weekSchedules).forEach(daySchedules => {
      daySchedules?.forEach(board => {
        board.timeSlots.forEach(slot => {
          const startTime = parseISO(slot.startTime);
          const timeKey = formatInTimeZone(startTime, board.gymTimezone || 'America/Los_Angeles', 'HH:mm');
          allSlots.add(timeKey);
        });
      });
    });
    return Array.from(allSlots).sort();
  }, [weekSchedules]);

  const formatTime = (timeKey) => {
    const [hours, minutes] = timeKey.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getDaySchedules = (day) => {
    const dateKey = format(day, 'yyyy-MM-dd');
    return weekSchedules[dateKey] || [];
  };

  const getTimeSlotStats = (day, timeKey) => {
    const schedules = getDaySchedules(day);
    if (!schedules.length) return { total: 0, reserved: 0, checkedIn: 0, blocked: 0, available: 0 };

    let total = 0;
    let reserved = 0;
    let checkedIn = 0;
    let blocked = 0;

    schedules.forEach(board => {
      const slot = board.timeSlots.find(s => {
        const startTime = parseISO(s.startTime);
        const slotTimeKey = formatInTimeZone(startTime, board.gymTimezone || 'America/Los_Angeles', 'HH:mm');
        return slotTimeKey === timeKey;
      });

      if (slot) {
        total++;
        if (slot.isBlocked) {
          blocked++;
        } else if (slot.reservationStatus === 'checked_in') {
          checkedIn++;
        } else if (slot.reservationId) {
          reserved++;
        }
      }
    });

    const available = total - reserved - checkedIn - blocked;
    return { total, reserved, checkedIn, blocked, available };
  };

  const getCellColor = (stats) => {
    if (stats.total === 0) return 'bg-gray-50';

    const occupancyRate = (stats.reserved + stats.checkedIn + stats.blocked) / stats.total;

    if (occupancyRate === 0) return 'bg-green-50 hover:bg-green-100';
    if (occupancyRate < 0.5) return 'bg-yellow-50 hover:bg-yellow-100';
    if (occupancyRate < 1) return 'bg-orange-50 hover:bg-orange-100';
    return 'bg-red-50 hover:bg-red-100';
  };

  const getCellBorder = (stats) => {
    if (stats.total === 0) return 'border-gray-200';

    const occupancyRate = (stats.reserved + stats.checkedIn + stats.blocked) / stats.total;

    if (occupancyRate === 0) return 'border-green-200';
    if (occupancyRate < 0.5) return 'border-yellow-200';
    if (occupancyRate < 1) return 'border-orange-200';
    return 'border-red-200';
  };

  const isToday = (day) => {
    const today = new Date();
    const dayStr = format(day, 'yyyy-MM-dd');
    const todayStr = format(today, 'yyyy-MM-dd');
    return dayStr === todayStr;
  };

  return (
    <div className="space-y-6">
      {/* Weekly Summary */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-lg shadow-md p-6 text-white">
        <h3 className="text-lg font-bold mb-4">Weekly Summary</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white/10 rounded-lg p-4">
            <div className="text-2xl font-bold">
              {Object.values(weekSchedules).reduce((sum, day) => sum + (day?.length || 0), 0)}
            </div>
            <div className="text-sm text-gray-300">Total Board Days</div>
          </div>
          <div className="bg-white/10 rounded-lg p-4">
            <div className="text-2xl font-bold">
              {Object.values(weekSchedules).reduce((sum, day) =>
                sum + (day?.reduce((s, b) => s + b.timeSlots.length, 0) || 0), 0
              )}
            </div>
            <div className="text-sm text-gray-300">Total Time Slots</div>
          </div>
          <div className="bg-white/10 rounded-lg p-4">
            <div className="text-2xl font-bold text-blue-300">
              {Object.values(weekSchedules).reduce((sum, day) =>
                sum + (day?.reduce((s, b) =>
                  s + b.timeSlots.filter(slot => slot.reservationId && !slot.isBlocked).length, 0
                ) || 0), 0
              )}
            </div>
            <div className="text-sm text-gray-300">Total Reservations</div>
          </div>
          <div className="bg-white/10 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-300">
              {Object.values(weekSchedules).reduce((sum, day) =>
                sum + (day?.reduce((s, b) =>
                  s + b.timeSlots.filter(slot => slot.reservationStatus === 'checked_in').length, 0
                ) || 0), 0
              )}
            </div>
            <div className="text-sm text-gray-300">Total Checked In</div>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="w-full">
          <div className="w-full">
            {/* Header Row - Days of Week */}
            <div className="flex border-b-2 border-gray-300 sticky top-0 bg-white z-10">
              {/* Time Column Header */}
              <div className="w-24 flex-shrink-0 p-4 font-semibold text-gray-700 border-r-2 border-gray-300">
                Time
              </div>

              {/* Day Headers */}
              {weekDays.map((day) => (
                <div
                  key={day.toISOString()}
                  className={`flex-1 min-w-[120px] p-4 border-r border-gray-200 ${
                    isToday(day) ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="text-center">
                    <div className="font-bold text-gray-900">
                      {format(day, 'EEE')}
                    </div>
                    <div className={`text-sm ${isToday(day) ? 'text-blue-600 font-semibold' : 'text-gray-600'}`}>
                      {format(day, 'MMM d')}
                    </div>
                    {isToday(day) && (
                      <div className="text-xs text-blue-600 font-medium mt-1">Today</div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Time Slot Rows */}
            <div className="max-h-[600px] overflow-y-auto">
              {timeSlots.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <p>No time slots scheduled for this week.</p>
                  <p className="text-sm mt-1">Time slots will be generated when users make reservations.</p>
                </div>
              ) : (
                timeSlots.map((timeKey) => (
                  <div key={timeKey} className="flex border-b border-gray-200">
                    {/* Time Label */}
                    <div className="w-24 flex-shrink-0 p-4 font-medium text-gray-600 border-r-2 border-gray-300 text-sm">
                      {formatTime(timeKey)}
                    </div>

                    {/* Day Cells */}
                    {weekDays.map((day) => {
                      const stats = getTimeSlotStats(day, timeKey);
                      const hasData = stats.total > 0;

                      return (
                        <div
                          key={`${day.toISOString()}-${timeKey}`}
                          className={`flex-1 min-w-[120px] p-2 border-r border-gray-200 ${
                            isToday(day) ? 'bg-blue-50/30' : ''
                          }`}
                        >
                          {hasData ? (
                            <button
                              onClick={() => onDayClick(day)}
                              className={`
                                w-full h-full min-h-[70px] rounded-lg p-3 border-2
                                ${getCellColor(stats)}
                                ${getCellBorder(stats)}
                                transition-all cursor-pointer
                                hover:shadow-md
                              `}
                            >
                              <div className="text-xs font-semibold text-gray-700 mb-2">
                                {stats.total} board{stats.total !== 1 ? 's' : ''}
                              </div>
                              <div className="space-y-1">
                                {stats.available > 0 && (
                                  <div className="text-xs text-green-700">
                                    ✓ {stats.available} available
                                  </div>
                                )}
                                {stats.reserved > 0 && (
                                  <div className="text-xs text-blue-700">
                                    • {stats.reserved} reserved
                                  </div>
                                )}
                                {stats.checkedIn > 0 && (
                                  <div className="text-xs text-green-700">
                                    ✓ {stats.checkedIn} checked in
                                  </div>
                                )}
                                {stats.blocked > 0 && (
                                  <div className="text-xs text-orange-700">
                                    ⊗ {stats.blocked} blocked
                                  </div>
                                )}
                              </div>
                            </button>
                          ) : (
                            <div className="h-full min-h-[70px] rounded-lg bg-gray-50 border border-gray-200"></div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-bold mb-4 text-gray-900">Occupancy Legend</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-green-50 border-2 border-green-200 rounded"></div>
            <span className="text-sm text-gray-700">All Available</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-yellow-50 border-2 border-yellow-200 rounded"></div>
            <span className="text-sm text-gray-700">&lt;50% Occupied</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-orange-50 border-2 border-orange-200 rounded"></div>
            <span className="text-sm text-gray-700">50-99% Occupied</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-red-50 border-2 border-red-200 rounded"></div>
            <span className="text-sm text-gray-700">Fully Booked</span>
          </div>
        </div>
      </div>

    </div>
  );
};

export default WeeklyScheduleView;
